<template>
  <div class="datetime-picker">
    <input v-model.lazy="year" ref="year" class="d f" title="year" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span class="sep">-</span>
    <input v-model="month" ref="month" class="d t" title="month" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span class="sep">-</span>
    <input v-model="day" ref="day" class="d t" title="day" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span class="sep" />
    <input v-model="hour" ref="hour" class="tm t" title="hour" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span class="sep">:</span>
    <input v-model="minute" ref="minute" class="tm t" title="minute" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span class="sep">:</span>
    <input v-model="second" ref="second" class="tm t" title="second" type="text" pattern="[0-9]*" input-mode="numeric" :disabled="disabled" @focus="onFocus" @keydown="keydown">
    <span v-if="!is24Hour" class="sep"/>
    <input v-if="!is24Hour" v-model="period" ref="period" class="tm p" title="period" type="text" pattern="[ap]m" :disabled="disabled" @focus="onFocus" @keydown="keydownPeriod">
  </div>
</template>

<style scoped lang="scss">
.datetime-picker {
  display: inline-block;
  width: max-content;
  max-width: 100%;
  font-size: 0.9em;
  white-space: nowrap;
  background: var(--input-bgcolor);
  border-radius: var(--br-sharp);
  border: 1px solid var(--input-border);
  padding: 0 0.5em;
  margin: 0 0.5em;
  
  input {
    min-width: 2.1ex;
    width: 2.5ex;
    border: none;
    background: transparent;
    padding: 0 0.1em;
    caret-color: transparent;
    text-align: center;
    margin: 0 3px; 
    
    &.f { width: 5ex;}
    &.p { width: 3ex;} // the font needs the .02ex to not clip the right side of the m in am pm
    
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
    
    &:focus {
//      outline: none;
      background: var(--accent-color);
    }
  }
  
  .sep {
    display: inline-block;
    min-width: 0;
    margin: 0 -1px;
  }
}

@media screen and (max-width: 768px) {
  .datetime-picker {
    padding: 0 0.25em;

    input {
      margin: 0 1px;
    }
  }
}
</style>
 
<script>
export default {
  name: 'DatetimePicker',
  
  props: {
    modelValue: {
      type: [Date, String],
      default: undefined
    },
    
    // Vue 2 compatibility for older call sites that still bind `value`.
    value: {
      type: [Date, String],
      default: undefined
    },
    
    is24Hour: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'input'],
  
  data() {
    return {
      day: undefined,
      month: undefined,
      year: undefined,
      hour: undefined,
      minute: undefined,
      second: undefined,
      period: 'am',
          
      selectedPart: undefined,
      selectedInputCount: 0, // the number of inputs (modifying keystrokes) made to the selected input since its selection
      
      internalValue: undefined,
    }
  },
  
  computed: {
    canSelectPrevPart() {
      return this.parts.indexOf(this.selectedPart) != 0;
    },
    canSelectNextPart() {
      return this.parts.indexOf(this.selectedPart) != (this.parts.length - 1);
    },
    
    parts() {
      let p = [
        'year',
        'month',
        'day',
        'hour',
        'minute',
        'second',
      ];

      if (!this.is24Hour) p.push('period');
      return p;
    },
    
    boundForSelectedPart() {
      switch (this.selectedPart) {
        case 'day':    return 31;
        case 'month':  return 12;
        case 'year':   return 3000;
        case 'hour':   return this.is24Hour ? 23 : 12;
        case 'minute':
        case 'second': return 59;
      }
      console.warn("unbounded date part", this.selectedPart);
      return Infinity;
    },
    
    date() {
      const hour = this.hourForDate();

      const now = new Date();

      return new Date(
        this.numberOrDefault(this.year, now.getFullYear()), 
        this.month !== undefined && this.month !== '' ? this.numberOrDefault(this.month, now.getMonth() + 1) - 1 : now.getMonth(), 
        this.numberOrDefault(this.day, now.getDate()), 
        hour, 
        this.numberOrDefault(this.minute, 0), 
        this.numberOrDefault(this.second, 0)
      );
    }
  },
  
  watch: {
    modelValue(v) {
      this.setInternalValueFromValue(v);
    },

    value(v) {
      if (this.modelValue === undefined) {
        this.setInternalValueFromValue(v);
      }
    },
    
    selectedPart(v) {
      this.selectedInputCount = 0;
    }
  },
  
  mounted() {
    this.setInternalValueFromValue(this.modelValue ?? this.value);
  },
  
  methods: {
    numberOrDefault(value, fallback) {
      const n = parseInt(value);
      return Number.isFinite(n) ? n : fallback;
    },

    hourForDate() {
      const hour = this.numberOrDefault(this.hour, 0);
      if (this.is24Hour) return hour;

      const hour12 = hour % 12;
      return this.period == 'pm' ? hour12 + 12 : hour12;
    },
    
    padtime(value){
      if (!value) 
        return '00';

      return ("0" + value.toString()).slice(-2)
    },
    
    setInternalValueFromValue(v) {      
      if (typeof v === 'string') {
        v = new Date(v);
      }
      
      this.internalValue = v;

      if (v) {
        this.day    = v.getDate()
        this.month  = v.getMonth() + 1
        this.year   = v.getFullYear()
        this.minute = this.padtime(v.getMinutes())
        this.second = this.padtime(v.getSeconds())
        
        if (!this.is24Hour) {
          const hour = v.getHours();
          this.period = hour < 12 ? 'am' : 'pm';
          this.hour = hour % 12 || 12;
        }
        else {
          this.period = 'am';
          this.hour = v.getHours();
        }
      }
      else {
        // Null date
        this.day    = '';
        this.month  = '';
        this.year   = '';
        this.hour   = '';
        this.minute = '';
        this.second = '';
        this.period = '';
        
      }
    },
    
    valueDidChange() {
      this.$emit('update:modelValue', this.date);
      this.$emit('input', this.date);
    },
    
    onFocus(e) {
      const elm = e.target;
      const found = !Object.keys(this.$refs).every((k) => {
        if (elm == this.$refs[k]) {
          this.selectedPart = k;
          return false;
        }
        return true;
      });

      if (!found) {
        console.warn("Unable to detect part being focused", e);
      }
    },
    
    blur() {
      const elm = this.$refs[this.selectedPart];
      elm?.blur();
    },
    
    incrementSelection(howFar) {
      let i = this.parts.indexOf(this.selectedPart);
      if (i < 0 || i >= this.parts.length)
        return;
      
      i = (i + howFar) % this.parts.length;
      if (i < 0) i = this.parts.length -1;
      
      const partName = this.parts[i];
      const elm = this.$refs[partName];

      // debug
      if (!elm) {
        console.warn(`could not increment selection to part ${i} (have ${this.parts.length} parts)`)
        return;
      }
      elm.focus();
      elm.select();
    },
    
    selectNextPart() {
      this.incrementSelection(1);
    },
    
    selectPrevPart() {
      this.incrementSelection(-1);
    },
    
    selectNextPartOrBlur() {
      if (this.canSelectNextPart)
        this.selectNextPart();
      else
        this.blur();
    },
    
    setPeriod(period) {
      period = period.toLowerCase();
      if (('am' !== period) && ('pm' !== period)) {
        console.error(`bad period ${period}`)
        return;
      }

      this.period = period;
      this.valueDidChange(); 
    },
    
    keydownPeriod(e) {
      if (!e) return;        
      switch (e.key) {
        case 'a': 
        case 'A': 
        case 'ArrowUp':
          this.setPeriod('am');
          e.preventDefault();
          break;
        case 'p': 
        case 'P': 
        case 'ArrowDown':
          this.setPeriod('pm');
          e.preventDefault();
          break;
        case 'Tab':
          if (!e.shiftKey) {
            if (!this.canSelectNextPart) {
              return true; // Allow default (tab to next control)
            }
            // Can select next && we're not going backwards
            // It's not currently possible to reach here, as the 
            // period is the last element in this control.
            this.selectPrevPart();
            e.preventDefault();
            return false;
          }
          /* fall through */
        case 'ArrowLeft': this.selectPrevPart(); e.preventDefault(); break;
        default: 
          // suppress & ignore
          e.preventDefault();
          e.stopPropagation();
          break; 
      }
    },
    
    keydown(e) {
      if (!e) return;        
      if (e.metaKey) return;
      if (e.ctrlKey) return;
      
      const w = e.which;
      // Don't block tab key
      //
      if (w==9/*tab*/ || w==12/*return*/) {
        if (e.shiftKey) {
          // going backwards
          if (!this.canSelectPrevPart) {
            return true; // allow default (tab to a previous control outside this component)
          }
          this.selectPrevPart();
          e.preventDefault();
          return false;
        }
        else {
          // going forward
          if (!this.canSelectNextPart) {
            return true; // allow default (tab to the next control outside this component)
          }
          this.selectNextPart();
          e.preventDefault();
          return false;
        }
      }
      
      e.preventDefault();
      e.stopPropagation();
      
      if (w == 65) { this.setPeriod('am'); return; }
      if (w == 80) { this.setPeriod('pm'); return; }
              
      // Arrow Keys
      //
      if (w == 37 /*left */) {
        this.selectPrevPart();
        return;
      }

      if (w == 39 /*right */) {
        this.selectNextPart()
        return;
      }

      if ((w == 38 /* up */ || w == 40 /* down */) && this.selectedPart == 'hour') {
        const delta = (e.shiftKey ? 10 : 1) * (w == 38 ? 1 : -1);
        const date = this.date;
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;

        date.setHours(date.getHours() + delta);
        if (Number.isNaN(date.getTime())) return;

        this.setInternalValueFromValue(date);
        this.valueDidChange();
        return;
      }
      
      if (w == 38 /* up */) {
        let bound = this.boundForSelectedPart;
        let inc = e.shiftKey ? 10 : 1
        let newValue = parseInt(this[this.selectedPart]) + inc;
        if (newValue <= bound) 
        {
          this[this.selectedPart] = newValue;
          this.valueDidChange();
        }
        return;
      }

      if (w == 40 /* down */) {
        const dec = e.shiftKey ? 10 : 1;
        if (this[this.selectedPart] - dec >= 0) {
          this[this.selectedPart] -= dec;
          this.valueDidChange();
        }
        return;
      }
      // '/' or ':'
      //
      if (e.key == ':' || e.key == '/') {
        this.selectNextPart();
        return;
      }
      
      // Delete
      if (w == 8 /*backspace*/) {
        const orig = this[this.selectedPart];
        console.debug(`delete ${orig} -> ${Math.floor(orig/10)}`)
      
        this[this.selectedPart] = Math.floor(orig / 10);
        this.valueDidChange();
        return;
      }
      
      // Check for digit
      //
      const digit = parseInt(e.key);
      if (isNaN(digit)) {
        // not a digit, not our problem.
        return;          
      }
      
      this.selectedInputCount++;

      //console.log(`key #${this.selectedInputCount} to ${this.selectedPart}`, digit);
      
      const wouldOverflow = digit * 10 > this.boundForSelectedPart;
      if (this.selectedInputCount == 1) {
        // first key since selection
        this[this.selectedPart] = digit;
        this.valueDidChange();
        
        if (wouldOverflow) {
          // Typed e.g. 6 in a box that can't support 60; we're done here
          this.selectNextPartOrBlur();
        }
      }
      else {
        // appending another value
        
        // typed the 2 in 12
        // Shift the current value by one magnitude and switch focus
        // to the next field.
        let newValue = this[this.selectedPart] * 10 + digit;
        if (newValue > this.boundForSelectedPart) {
          if (this.selectedPart == 'year') {
            // "19021 is out of bounds". 2-> 1902. 1-> 19021. Make it 21
            const newValueStr = newValue.toString();
            newValue = parseInt(newValueStr.slice(newValueStr.length -this.selectedInputCount));
          }
          else {
            console.debug(`append ${newValue} is out of bounds`);
            return;
          }
        }

        this[this.selectedPart] = newValue;
        this.valueDidChange();

        // Can we possibly add another digit?
        if (newValue * 10 > this.boundForSelectedPart)
          this.selectNextPartOrBlur();
      }
    },
  }
}
</script>
